import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft } from "lucide-react";

// Post-confirmation landing for newsletter subscribers arriving from Kit's
// double-opt-in email. Kept intentionally minimal: reuses the existing brand
// header (via App.js <Layout>), the same eyebrow/heading/body/button pattern
// as NotFound / ComingSoon, no new photography, no new components.
//
// Emits <meta name="robots" content="noindex,nofollow"> because this page is
// only meaningful for confirmed subscribers arriving from a private email link
// and must never surface in search results.
export default function NewsletterConfirmed() {
  const { t } = useTranslation();
  useEffect(() => { window.scrollTo(0, 0); }, []);

  useEffect(() => {
    const originalTitle = document.title;
    document.title = t("newsletterConfirmed.title");

    let robots = document.querySelector('meta[name="robots"]');
    const created = !robots;
    const previousContent = robots ? robots.getAttribute("content") : null;
    if (!robots) {
      robots = document.createElement("meta");
      robots.setAttribute("name", "robots");
      document.head.appendChild(robots);
    }
    robots.setAttribute("content", "noindex,nofollow");

    return () => {
      document.title = originalTitle;
      if (created) {
        robots.parentNode && robots.parentNode.removeChild(robots);
      } else if (previousContent) {
        robots.setAttribute("content", previousContent);
      }
    };
  }, [t]);

  return (
    <div className="min-h-[70vh] flex items-center justify-center py-24" data-testid="newsletter-confirmed-page">
      <div className="max-w-xl mx-auto px-6 text-center space-y-8">
        <span className="text-[10px] font-sans tracking-[0.3em] uppercase text-[var(--accent-bronze)] font-semibold block" data-testid="newsletter-confirmed-eyebrow">
          {t("newsletterConfirmed.eyebrow")}
        </span>
        <h1 className="text-4xl sm:text-5xl font-serif text-primary-custom tracking-tight leading-tight" data-testid="newsletter-confirmed-heading">
          {t("newsletterConfirmed.heading")}
        </h1>
        <p className="font-serif text-base sm:text-lg italic text-secondary-custom leading-relaxed font-light" data-testid="newsletter-confirmed-body">
          {t("newsletterConfirmed.body")}
        </p>
        <div className="pt-2">
          <Link to="/" data-testid="newsletter-confirmed-home-link" className="inline-flex items-center gap-2 border border-primary px-8 py-3 text-xs uppercase tracking-[0.2em] font-medium hover:bg-[var(--accent-bronze)] hover:text-white hover:border-[var(--accent-bronze)] transition-all duration-300">
            <ArrowLeft className="w-3.5 h-3.5" />
            <span>{t("newsletterConfirmed.returnHome")}</span>
          </Link>
        </div>
      </div>
    </div>
  );
}
